import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Dialog, DialogContent, Stack, Typography } from "@mui/material";

import { Task } from "api/types/users";

interface TechCheckTaskProps {
  data: Task;
  clientId?: string;
  setCurrentTask: (task: Task | null) => void;
}

/**
 * Launcher for the Tech Check task.
 *
 * The tech-check UI lives in the video service (svc-video) so the client sees
 * the same in-call frame they'll experience on their first real session. This
 * component just redirects the browser to that experience and shows a momentary
 * "Opening tech check…" dialog while the navigation happens.
 */
const TechCheckTask: React.FC<TechCheckTaskProps> = ({ data, clientId, setCurrentTask }) => {
  const { t } = useTranslation(["dashboard"]);

  useEffect(() => {
    // Defensive: this launcher only knows how to open a tech_check task. If it
    // is ever rendered for another task type, close it rather than redirecting
    // to a tech-check URL built from an unrelated task id.
    if (data.type !== "tech_check") {
      setCurrentTask(null);
      return;
    }

    // `VIDEO_URL` is the app-wide handle for svc-video — the same variable
    // VideoRouteGuard and useJoinAppointment already read, and the one the
    // deploy workflows actually write (see preview-eks.yml:486). vite.config
    // inlines the whole env file into `process.env` with an empty prefix, so
    // other names would resolve fine if they were set; VIDEO_URL is used
    // because it's the name that *is* set, not because it's the only one that
    // would work.
    const baseUrl = process.env.VIDEO_URL || "";

    if (!baseUrl) {
      console.warn("VIDEO_URL is not configured — cannot launch tech check. Closing task.");
      setCurrentTask(null);
      return;
    }

    const params = new URLSearchParams();
    if (clientId) params.set("clientId", clientId);
    const url = `${baseUrl.replace(/\/$/, "")}/tech-check/${encodeURIComponent(
      String(data.id),
    )}${params.toString() ? `?${params.toString()}` : ""}`;

    window.location.assign(url);
  }, [data.id, data.type, clientId, setCurrentTask]);

  return (
    <Dialog open onClose={() => setCurrentTask(null)} aria-label={t("parent.myTasks.techCheck.opening")}>
      <DialogContent>
        <Stack alignItems="center" spacing={2} sx={{ py: 2, px: 4 }}>
          <CircularProgress />
          <Typography>{t("parent.myTasks.techCheck.opening")}</Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default TechCheckTask;
